import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { JWTPayload, decodeJwt } from 'jose';
import { isAxiosError } from 'axios';
import { useSelector } from 'react-redux';
import { sessionExpiredToast } from 'components/Toast';
import { FullScreenSpinner } from 'components/Spinner';
import { isSaaS } from 'utils/isSaaS';
import { instance } from 'api/request';
import { selectEnabled } from 'store/common/toggle';
import { IAuthProvider } from './type';
import { CurrentProvider } from './helpers';

interface IAuthContext {
  token: string | undefined;
  claim?: JWTPayload;
  provider: IAuthProvider;
}

const AuthData = React.createContext<IAuthContext>({} as IAuthContext);

export const AuthProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [token, setToken] = useState<string | undefined>();
  const [isLoading, setLoading] = useState(true);
  const isAnonymousDisabled = useSelector(selectEnabled('denyAnonymous'));
  const lastTokenRef = useRef<string | undefined>(undefined);
  const authRecoveryInProgressRef = useRef(false);

  const currentProvider = useMemo<IAuthProvider>(() => {
    const provider = new CurrentProvider(!isAnonymousDisabled).getCurrentProvider();

    provider.setAuthInterceptor(instance);
    window.currentProvider = provider;

    return provider;
  }, [isAnonymousDisabled]);

  const claim = useMemo(() => (token ? decodeJwt(token as string) : undefined), [token]);

  const authorize = useCallback(async () => {
    try {
      await currentProvider.init();
      const initialToken = currentProvider.getToken();
      lastTokenRef.current = initialToken;
      setToken(initialToken);
      if (isSaaS()) {
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
    }
  }, [currentProvider]);

  useEffect(() => {
    authorize();
  }, [authorize]);

  useEffect(() => {
    lastTokenRef.current = currentProvider.getToken();

    const interceptorId = instance.interceptors.response.use(
      response => {
        const newToken = currentProvider.getToken();
        if (newToken !== lastTokenRef.current) {
          lastTokenRef.current = newToken;
          setToken(newToken);
        }

        return response;
      },
      async (error: unknown) => {
        const hadKnownSession = Boolean(
          lastTokenRef.current || currentProvider.getToken(),
        );
        const isUnauthorizedResponse =
          isAxiosError(error) && error.response?.status === 401;
        const isSilentAuthFailure = typeof error === 'undefined';
        const shouldRecoverSession =
          hadKnownSession && (isSilentAuthFailure || isUnauthorizedResponse);

        if (shouldRecoverSession) {
          authRecoveryInProgressRef.current = true;
          sessionExpiredToast();
        }

        return Promise.reject(error);
      },
    );

    return () => {
      instance.interceptors.response.eject(interceptorId);
    };
  }, [currentProvider]);

  const value = useMemo(
    (): IAuthContext => ({
      token,
      claim: token ? claim : undefined,
      provider: currentProvider,
    }),
    [claim, currentProvider, token],
  );

  if (isLoading && isSaaS()) return <FullScreenSpinner />;

  return <AuthData.Provider value={value}>{children}</AuthData.Provider>;
};

export const useAuth = () => useContext(AuthData);
